import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * The z-index budget for one modal layer: an overlay (the backdrop) and the
 * content pane above it.
 *
 * `Dialog` and `Sheet` both render an overlay/content pair from the same
 * Radix primitive, historically both fixed at `z-40` / `z-50`. That is
 * correct for exactly one open layer. It breaks the moment a second one
 * opens on top of the first — the cancel-shift confirmation over the shift
 * inspector, for instance — because within one CSS stacking context,
 * z-index GROUPS paint before DOM order does: the nested overlay's `z-40`
 * sits in the same paint group as the outer dialog's `z-40`, both below the
 * `z-50` group the two CONTENTS share, so the nested overlay paints under
 * the outer content instead of dimming it. The outer surface stayed
 * undimmed and legible around the edges of what is supposed to be the only
 * actionable thing on screen.
 *
 * The fix claims the next whole layer for every dialog/sheet that OPENS, in
 * open order — not JSX nesting order. A confirmation is usually rendered as
 * a SIBLING of the surface it confirms over (`ShiftInspector`'s inspector
 * `Dialog` and its cancel-shift `Dialog` are both top-level returns from the
 * same component, neither is inside the other's `children`), so there is no
 * component-tree ancestry to key off. Open order tracks nesting order in
 * practice anyway: nothing opens a second dialog before the first one that
 * hosts the control that opens it.
 *
 * Layer N gets `overlayZ = BASE_OVERLAY_Z + N * LAYER_STEP` and
 * `contentZ = BASE_CONTENT_Z + N * LAYER_STEP`. The step (20) is wide enough
 * that a layer's overlay always beats the PREVIOUS layer's content
 * (60 > 50, 80 > 70, …) at any depth, with no cap on how many can stack.
 * Layer 0 keeps the exact historical values (40/50), so a single open
 * dialog or sheet is unaffected.
 */
const BASE_OVERLAY_Z = 40;
const BASE_CONTENT_Z = 50;
const LAYER_STEP = 20;

export interface OverlayZIndex {
  readonly overlayZ: number;
  readonly contentZ: number;
}

let nextId = 0;
let openStack: readonly number[] = [];
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): readonly number[] {
  return openStack;
}

function claimLayer(): number {
  const id = nextId;
  nextId += 1;
  openStack = [...openStack, id];
  listeners.forEach((listener) => listener());
  return id;
}

function releaseLayer(id: number): void {
  openStack = openStack.filter((entry) => entry !== id);
  listeners.forEach((listener) => listener());
}

/**
 * Registers one open/closed overlay layer for as long as `isOpen` is true,
 * and returns the z-index pair it should paint at. Call unconditionally —
 * it is inert while `isOpen` is false, and re-claims a (possibly different)
 * layer every time `isOpen` turns true again.
 */
export function useOverlayZIndex(isOpen: boolean): OverlayZIndex {
  const idRef = useRef<number | null>(null);
  // Re-renders whenever ANY layer opens or closes, so a layer whose position
  // in the stack shifts (e.g. one below it closes first) repaints at the
  // right z-index rather than a stale one.
  const stack = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const id = claimLayer();
    idRef.current = id;
    return () => {
      releaseLayer(id);
      idRef.current = null;
    };
  }, [isOpen]);

  const id = idRef.current;
  const depth = id === null ? 0 : Math.max(0, stack.indexOf(id));

  return {
    overlayZ: BASE_OVERLAY_Z + depth * LAYER_STEP,
    contentZ: BASE_CONTENT_Z + depth * LAYER_STEP,
  };
}

/**
 * Where a popper paints: `Select`, `Combobox` and `Popover` all portal to
 * `<body>`, so they are not inside the stacking context of the dialog that
 * opened them.
 *
 * A fixed point comfortably above any realistic dialog/sheet stack (a stack
 * five layers deep would still only reach 140). It has to be: a select
 * opened inside a stacked dialog was painting BEHIND that dialog's content,
 * because the poppers sat at a flat `z-50` — layer 0's content — while the
 * second open layer had already claimed 70.
 *
 * A popper is always owned by whatever is on top, so a single number above
 * the whole band is correct; it never has to interleave with the stack.
 */
export const POPPER_Z_INDEX = 1000;
